import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import {
  _fetchCandidateDetails,
  _fetchCandidateDocuments,
  _handleCandidateDetailsSubmit,
  _updateCandidateDocuments,
  _updateCandidatePaymentDetails,
} from "@/services/candidateServices";
import {
  getCandidatePersonalFallback,
  saveCandidatePersonalFallback,
} from "@/utilities/candidatePersonalFallback";
import { handleAPICatch, showSuccessToast } from "@/utilities/utils";

const initialState = {
  createData: {
    title: null,
    profilePhoto: null,
    firstName: "",
    lastName: "",
    gender: "",
    mobile: "",
    email: "",
    dateOfBirth: null,
    postCode: null,
    address: null,
    county: null,
    region: null,
    clientLat: null,
    clientLng: null,
    role: null,
    setting: null,
    referral: null,
    skills: [],
    status: null,
    band: null,
    gmcNumber: null,
    consultant: null,
  },
  editData: {},
  editLoading: true,
  candidateComplianceDocuments: {},
  selectedCandidateComplianceDocuments: [],
  isComplianceModalOpen: false,
  isResumeUploaded: false,
};

export const handleFetchCandidateDetails = createAsyncThunk(
  "candidateCreate/handleFetchCandidateDetails",
  async (data, { dispatch }) => {
    dispatch(toggleEditLoading(true));
    const response = _fetchCandidateDetails(data);
    response
      .then((res) => {
        const candidateDetails = res?.data?.candidate_details || {};
        const candidateId =
          candidateDetails?.candidate_id || data?.candidate_id;
        const fallbackDetails = getCandidatePersonalFallback(candidateId);
        dispatch(
          updateCandidateEditData({
            ...candidateDetails,
            candidate_id: candidateId,
            candidate_dob:
              candidateDetails?.candidate_dob || fallbackDetails?.candidate_dob,
            candidate_skill_ids:
              candidateDetails?.candidate_skill_ids ||
              fallbackDetails?.candidate_skill_ids,
          }),
        );
      })
      .catch((error) => {
        console.log("Error fetching candidate details: ", error);
        handleAPICatch(error);
      })
      .finally(() => {
        dispatch(toggleEditLoading(false));
      });
  },
);

export const handleUpdateCandidatePaymentDetails = createAsyncThunk(
  "candidateCreate/handleUpdateCandidatePaymentDetails",
  async (data, { dispatch }) => {
    const callback = data.callback;
    const setSubmitting = data.setSubmitting;
    if (setSubmitting) {
      delete data.setSubmitting;
    }
    if (callback) {
      delete data.callback;
    }
    const response = _updateCandidatePaymentDetails(data);
    response
      .then((res) => {
        console.log("Updated Candidate Payment Details: ", res);
        callback && callback();
      })
      .catch((error) => {
        handleAPICatch(error);
      })
      .finally(() => {
        if (setSubmitting) {
          setSubmitting(false);
        }
      });
  },
);

export const handleFetchCandidateDocuments = createAsyncThunk(
  "candidateCreate/handleFetchCandidateDocuments",
  async (data, { dispatch }) => {
    const response = _fetchCandidateDocuments(data);
    response
      .then((res) => {
        const candidateDocuments = res?.data?.docs || {};
        // const candidateDocuments = res?.data?.candidate_documents || {};
        dispatch(updateCandidateComplianceDocuments(candidateDocuments));
        dispatch(toggleEditLoading(false));
      })
      .catch((error) => {
        handleAPICatch(error);
      });
  },
);

export const updateCandidateCompliance = createAsyncThunk(
  "candidateCreate/updateCandidateCompliance",
  async (data, { dispatch, getState }) => {
    const { postData, setSubmitting, callback } = data;
    setSubmitting(true);
    try {
      let response = await _updateCandidateDocuments(postData);
      showSuccessToast("Compliance has been successfully updated !");
      dispatch(toggleComplianceModal(false));
      callback && callback();
    } catch (error) {
      handleAPICatch(error);
    } finally {
      setSubmitting(false);
    }
  },
);

export const handleCandidateDetailsSubmit = createAsyncThunk(
  "candidateCreate/handleCandidateDetailsSubmit",
  async (data, { dispatch }) => {
    const callback = data.callback;
    if (callback) {
      delete data.callback;
    }
    const response = _handleCandidateDetailsSubmit(data);
    response
      .then((res) => {
        console.log("Submitted Candidate Details: ", res);
        const candidateId = res?.data?.candidate_id || data?.candidate_id;
        saveCandidatePersonalFallback(candidateId, {
          candidate_dob: data?.candidate_dob,
          candidate_skill_ids: data?.candidate_skill_ids,
        });
        callback && callback(candidateId);
      })
      .catch((error) => {
        handleAPICatch(error);
      });
  },
);

const candidateCreateSlice = createSlice({
  name: "candidateCreate",
  initialState,
  reducers: {
    updateCandidateCreateData: (state, action) => {
      if (action.payload) {
        state.createData = { ...state.createData, ...action.payload };
      } else {
        state.createData = initialState.createData;
      }
    },
    updateCandidateEditData: (state, action) => {
      if (action.payload) {
        state.editData = { ...state.editData, ...action.payload };
      } else {
        state.editData = initialState.editData;
      }
    },
    updateCandidateComplianceDocuments: (state, action) => {
      state.candidateComplianceDocuments = {
        ...state.candidateComplianceDocuments,
        ...action.payload,
      };
    },
    toggleEditLoading: (state, action) => {
      if (action.payload === undefined) {
        state.editLoading = !state.editLoading;
      } else {
        state.editLoading = action.payload;
      }
    },
    updateSelectedDocuments: (state, action) => {
      state.selectedCandidateComplianceDocuments = action.payload;
    },
    toggleComplianceModal: (state, action) => {
      if (action.payload === undefined) {
        state.isComplianceModalOpen = !state.isComplianceModalOpen;
      } else {
        state.isComplianceModalOpen = action.payload;
      }
    },
    toggleResumeUploaded: (state, action) => {
      state.isResumeUploaded = action.payload ?? !state.isResumeUploaded;
    },
  },
});

export const {
  updateCandidateCreateData,
  updateCandidateEditData,
  updateCandidateComplianceDocuments,
  toggleEditLoading,
  updateSelectedDocuments,
  toggleComplianceModal,
  toggleResumeUploaded,
} = candidateCreateSlice.actions;

export default candidateCreateSlice.reducer;
